import { logger } from '@user-portal/logger';
import { ERROR_LEVELS, SENTRY_CABINET } from '@user-portal/sentry-utils';
import { Main } from '@user-portal/wikr-analytics';
import { SHOP_SUBSCRIPTIONS } from 'analytics/constants/ids';
import {
    submitDoublePurchaseModal,
    trackContactSupportClick,
    trackContinueBuyClick,
    trackDeclinePaymentModalLoad,
    trackDoublePurchaseModalLoad,
    trackGoToMainClick,
    trackShopPageLoad,
    trackTryAgainClick,
} from 'analytics/trackers/shop';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PRIVATE } from 'router/routes';

import api from 'api';

import { selectDeepLink } from 'store/deepLink/selectors';
import { ModalName } from 'store/modals/types';
import { updateUserProductCodes } from 'store/user/actions';
import { selectCurrentUser } from 'store/user/selectors';

import { MOCKED_PRODUCTS } from 'constants/products/upsell';

import { showFrontChat } from 'services/FrontChat';
import sentry from 'services/Sentry/SentryInstance';

import { useDispatch, useScreenLoadTracker, useSelector } from 'hooks';
import { useModals } from 'hooks/modals';
import useNotify from 'hooks/notification/useNotify';

import { doesObjectContainKey } from 'helpers/doesObjectContainKey';
import { getPriceFromCents } from 'helpers/prices';

import { UpsellSubscriptionProductType } from 'types/subscription';

import Product from './components/Product';
import useProductInfo from './useProductInfo';

import * as S from './styled';

type ShopProps = {
    product: UpsellSubscriptionProductType;
    productCode: keyof typeof MOCKED_PRODUCTS;
};

type PaymentParamsType = { product_id: string; product_code: string };

type PaymentResponseType = {
    order: any;
    error?: any;
};

const Shop = ({ productCode, product }: ShopProps) => {
    useScreenLoadTracker(trackShopPageLoad(productCode));

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { openModal, closeModal } = useModals();
    const { notifySuccess } = useNotify();
    const [isProcessing, setProcessingStatus] = useState(false);
    const { subProductsCodes, title, userProductsTitle } = useProductInfo(product.product_code);
    const deepLinkUrl = useSelector(selectDeepLink);

    const user = useSelector(selectCurrentUser);
    const currentPrice = product.actual_price;

    const hasAnySubProductCode = (subProductCodesArray: string[], productList: Record<string, boolean> | null) => {
        return subProductCodesArray.some((code) => doesObjectContainKey(productList, code));
    };

    const displayDoublePurchaseModal = () => {
        openModal(ModalName.universalModal, {
            trackLoad: trackDoublePurchaseModalLoad(productCode),
            image: 'rising-hands',
            captions: {
                title: t('payment.error.doublePurchase.subscription.title', {
                    name: userProductsTitle,
                }),
                subtitle: t('payment.error.doublePurchase.description'),
                secondaryButton: t('subscription.button.text.goToApp').toUpperCase(),
            },
            onSecondaryClick: () => {
                submitDoublePurchaseModal(productCode);

                Object.assign(document.createElement('a'), {
                    target: '_blank',
                    rel: 'noopener noreferrer',
                    href: deepLinkUrl,
                }).click();
            },
        });
    };

    const checkAndDisplayModal = (
        productList: Record<string, boolean> | null,
        productCode: string,
        subProductsCodes: string[]
    ) => {
        if (doesObjectContainKey(productList, productCode) || hasAnySubProductCode(subProductsCodes, productList)) {
            displayDoublePurchaseModal();
        }
    };

    useEffect(() => {
        checkAndDisplayModal(user.product_list, productCode, subProductsCodes);
    }, []);

    const handlePurchase = async () => {
        setProcessingStatus(true);

        try {
            const response = await api.payments.oneClickPayment<PaymentParamsType, PaymentResponseType>({
                product_id: product.solid_id,
                product_code: product.product_code,
            });

            if (response?.error) {
                logger.error(`MB Portal: ${response?.error?.code?.recommended_message_for_user || 'PAYMENT'}`, {
                    error: response.error,
                });

                sentry.logError(
                    new Error(
                        `${response?.error?.code?.recommended_message_for_user || 'PAYMENT'}: ${response?.error.code}`
                    ),
                    SENTRY_CABINET,
                    ERROR_LEVELS.CRITICAL,
                    response?.error
                );

                setProcessingStatus(false);
                displayPaymentDeclinedModal();

                return;
            }

            dispatch(
                updateUserProductCodes({
                    [product.product_code]: true,
                })
            );

            notifySuccess('Your payment was successful! Thank you!');

            const { order } = response;

            const analyticData = {
                currency: order.currency,
                price: getPriceFromCents(currentPrice),
                screen_id: SHOP_SUBSCRIPTIONS,
                ab_test_name: user.ab_test_name,
                order_type: 'subscription',
                value: product.ltv,
                content_id: product.content_id,
                tariff: product.solid_id,
                payment: 'paypal',
                order_id: order.order_id,
                subscription_id: order.subscription_id,
            };

            Main.purchase(analyticData);

            navigate(PRIVATE.MAIN.path);
        } catch (error: any) {
            const { response } = error || {};

            setProcessingStatus(false);

            // Double purchase back-end validation
            if (response?.data?.error_product_code) {
                displayDoublePurchaseModal();
            } else {
                displayPaymentDeclinedModal();
            }
        }
    };

    const displayPaymentDeclinedModal = () => {
        openModal(ModalName.universalModal, {
            image: 'decline',
            trackLoad: trackDeclinePaymentModalLoad(productCode),
            captions: {
                title: t('basics.payment.decline'),
                extraText: {
                    text: t('basics.appError'),
                    color: 'text-secondary',
                },
                primaryButton: t('basics.contactSupport'),
                secondaryButton: t('basics.goToMain'),
                tertiaryButton: t('basics.tryAgain'),
            },
            onPrimaryClick: () => {
                trackContactSupportClick(productCode);
                showFrontChat();
            },
            onSecondaryClick: () => {
                trackGoToMainClick(productCode);
                closeModal();
            },
            onTertiaryClick: () => {
                trackTryAgainClick(productCode);
                closeModal();
                handlePurchase();
            },
        });
    };

    return (
        <S.ProductWrapper>
            <Helmet title={title} />

            {product && (
                <Product
                    handlePurchase={() => {
                        trackContinueBuyClick(productCode);
                        handlePurchase();
                    }}
                    name={title}
                    price={currentPrice}
                    isProcessing={isProcessing}
                    product={product}
                />
            )}
        </S.ProductWrapper>
    );
};

export default Shop;
